/**
 * Account hooks — TanStack Query wrappers for the accounts API.
 *
 * Accounts are nested under clients: /api/v1/clients/:clientId/accounts
 *
 * Hooks:
 * - useAccounts(clientId)          — list accounts for a client
 * - useCreateAccount(clientId)     — mutation to create a new account
 * - useUpdateAccount(clientId)     — mutation to update an existing account
 *
 * Note: the API returns snake_case rows (AccountRow), NOT the camelCase Account interface.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'
import type { AccountRow, CreateAccountDTO, UpdateAccountDTO } from '@1klick/types'

// ── Response types ────────────────────────────────────────────────────────────

/** API returns { data: AccountRow[], total: number } for list */
interface AccountsListResponse {
  data: AccountRow[]
  total: number
}

/** API returns { data: AccountRow } for single account */
interface AccountDetailResponse {
  data: AccountRow
}

// ── Query key factory ─────────────────────────────────────────────────────────

export const accountKeys = {
  all: ['accounts'] as const,
  byClient: (clientId: string) => [...accountKeys.all, 'client', clientId] as const,
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Fetch all accounts for a given client.
 * Disabled when `clientId` is falsy.
 */
export function useAccounts(clientId: string) {
  return useQuery<AccountRow[]>({
    queryKey: accountKeys.byClient(clientId),
    queryFn: async () => {
      const res = await apiGet<AccountsListResponse>(
        `/api/v1/clients/${clientId}/accounts`,
      )
      return res.data
    },
    enabled: !!clientId,
  })
}

/**
 * Mutation to create a new account under a client.
 * Invalidates the accounts list for that client on success.
 */
export function useCreateAccount(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation<AccountRow, Error, CreateAccountDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<AccountDetailResponse>(
        `/api/v1/clients/${clientId}/accounts`,
        data,
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.byClient(clientId) })
    },
  })
}

/**
 * Mutation to update an account.
 * Invalidates the accounts list for the parent client on success.
 */
export function useUpdateAccount(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation<AccountRow, Error, { accountId: string; data: UpdateAccountDTO }>({
    mutationFn: async ({ accountId, data }) => {
      const res = await apiPut<AccountDetailResponse>(
        `/api/v1/clients/${clientId}/accounts/${accountId}`,
        data,
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.byClient(clientId) })
    },
  })
}
